/**
 * Rate-limited request queue.
 * Processes async tasks sequentially with a configurable delay between them.
 * Prevents WB API rate limit errors (e.g. max 4 requests per minute).
 */

interface QueueItem<T = unknown> {
  fn: () => Promise<T>
  resolve: (value: T) => void
  reject: (reason: unknown) => void
}

export class RequestQueue {
  private readonly queue: QueueItem[] = []
  private processing = false
  private readonly delayMs: number

  constructor(requestsPerMinute: number) {
    this.delayMs = Math.ceil(60_000 / requestsPerMinute)
  }

  enqueue<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queue.push({ fn, resolve, reject } as QueueItem)
      this.process()
    })
  }

  private async process(): Promise<void> {
    if (this.processing) 
      return
    this.processing = true

    while (this.queue.length > 0) {
      const item = this.queue.shift()!
      try {
        const result = await item.fn()
        item.resolve(result)
      }
      catch (error) {
        item.reject(error)
      }

      if (this.queue.length > 0) {
        await new Promise(r => setTimeout(r, this.delayMs))
      }
    }

    this.processing = false
  }
}
